import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
// Import conditionally for better SSR compatibility
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import enUS from './locales/en-US.json';
import enGB from './locales/en-GB.json';
import enCA from './locales/en-CA.json';

// Check if we're in a browser environment (for better SSR handling)
const isBrowser = typeof window !== 'undefined';

// Create a separate instance for SSR/static data that's immediately available
// This ensures that translations are available for route configurations
export const staticI18n = i18n.createInstance();
staticI18n.init({
  lng: 'en-US', // Always use the default language for SSR
  resources: {
    'en-US': {
      translation: enUS,
    },
    'en-GB': {
      translation: enGB,
    },
    'en-CA': {
      translation: enCA,
    },
  },
  fallbackLng: 'en-US',
  interpolation: {
    escapeValue: false,
  },
});

// Handle initial language detection manually for better control
let initialLanguage = 'en-US'; // Default fallback
if (isBrowser) {
  // Priority: localStorage > browser language > default
  const storedLanguage = localStorage.getItem('trackplicant-language');
  
  if (storedLanguage && ['en-US', 'en-GB', 'en-CA'].includes(storedLanguage)) {
    initialLanguage = storedLanguage;
  } else {
    // Check browser language
    const browserLang = navigator.language;
    
    // Map browser language to our supported languages
    if (browserLang) {
      const lowerLang = browserLang.toLowerCase();
      if (lowerLang === 'en-ca') {
        initialLanguage = 'en-CA';
      } else if (lowerLang === 'en-gb' || lowerLang === 'en-au' || lowerLang === 'en-nz' || lowerLang === 'en-ie' || lowerLang === 'en-za') {
        initialLanguage = 'en-GB';
      }
    }
    
    // Store the detected language
    localStorage.setItem('trackplicant-language', initialLanguage);
  }
}

// Set up the chain for initializing i18next
const i18nextSetup = i18n.use(initReactI18next);

// Use language detector as backup, but our manual detection takes precedence
if (isBrowser) {
  i18nextSetup.use(LanguageDetector);
}

// Initialize i18next
i18nextSetup.init({
  // Available languages
  resources: {
    'en-US': {
      translation: enUS,
    },
    'en-GB': {
      translation: enGB,
    },
    'en-CA': {
      translation: enCA,
    },
  },
  // Explicitly set the initial language we detected
  lng: initialLanguage,
  // Fallback language
  fallbackLng: 'en-US',
  // Debug mode (disable in production)
  debug: isBrowser && import.meta.env.DEV,
  // Default namespace
  defaultNS: 'translation',
  // Interpolation configuration
  interpolation: {
    // React already escapes values
    escapeValue: false,
  },
  // Keep track of available languages
  supportedLngs: ['en-US', 'en-GB', 'en-CA'],
  // Detection options (used as backup)
  ...(isBrowser && {
    detection: {
      // Order of detection (we prioritize localStorage)
      order: ['localStorage', 'querystring', 'navigator', 'htmlTag'],
      // Cache user language on localStorage
      caches: ['localStorage'],
      // Store in localStorage under this key
      lookupLocalStorage: 'trackplicant-language',
      // Use querystring parameter
      lookupQuerystring: 'lng',
      // NOTE: checkWhitelist is deprecated, using a custom way to match partial language codes
      // lookupFromPathIndex and lowerCaseLng are custom options
    },
  }),
});

export default i18n;